import { useState } from "react";
import { fingerFor, type Bundle, type Lesson } from "@otto/lehrgang";
import type { Modifier } from "../lib/modifier.js";
import { cssVar } from "../../../shared/lib/cssVar.js";
import own from "./Keyboard.module.css";

/**
 * Bildschirmtastatur wie im Prototyp: gelernte Tasten in der Fingerfarbe
 * gefüllt, ungelernte zurückgenommen, die gesuchte mit doppeltem Ring und
 * pulsierend.
 *
 * Die Tasten sind anklickbar – ein Kind, dessen Tastatur im Koffer nicht
 * funktioniert, kann die Stunde trotzdem mitmachen. Das ist ein Knopf, kein
 * Eingabefeld; die Bildschirmtastatur des iPads klappt dadurch nicht auf.
 *
 * Umschalt- und Feststelltaste verhalten sich wie auf einer echten Tastatur:
 * Umschalt gilt für genau einen Anschlag, die Feststelltaste rastet ein, und
 * beide zusammen heben sich auf. Ohne sie wäre Kapitel 5 für genau das Kind
 * nicht machbar, für das die Bildschirmtastatur gedacht ist.
 */
export function Keyboard({
  bundle,
  lesson,
  expected,
  lastKey,
  onKey,
  modifier,
}: {
  bundle: Bundle;
  lesson: Lesson;
  expected: string | null;
  lastKey: string | null;
  onKey: (key: string, modifiers?: { readonly shiftKey?: boolean }) => void;
  /** Welche Zusatztaste die Lektion für den gesuchten Buchstaben verlangt. */
  modifier: Modifier | null;
}) {
  // Beide Zustände liegen hier, weil sie nur die Tastatur betreffen: welchen Weg
  // ein Anschlag genommen hat, sagt sie beim Drücken mit - der Trainer muss den
  // Zustand nicht kennen.
  const [shift, setShift] = useState(false);
  const [caps, setCaps] = useState(false);
  const uppercase = shift !== caps;

  // Groß und klein sind dieselbe Taste: die Reihen im Bundle sind klein
  // geschrieben, ein erwartetes "A" muss trotzdem auf dem a aufleuchten.
  const expectedKey = expected === null ? null : expected.toLowerCase();
  const learned = new Set(lesson.keys.map((k) => k.toLowerCase()));

  // Nur solange der Großbuchstabe noch nicht vorbereitet ist – sonst pulsieren
  // zwei Tasten gleichzeitig und das Kind weiß nicht, welche zuerst.
  //
  // Vorher pulsierte hier immer eine Umschalttaste. In Lektion 10 war das die
  // Taste, die dort als Vertipper zählt – die Tastatur zeigte auf den Weg, den
  // die Lektion gerade nicht übt.
  const wanted = uppercase ? null : modifier;
  const wantedSide = wanted?.kind === "shift" ? wanted.side : null;
  const wantedCaps = wanted?.kind === "capsLock";

  const press = (key: string) => {
    // Die Tastatur weiß selbst, auf welchem Weg der Großbuchstabe entsteht -
    // und sagt es weiter, damit Lektion 10 auch hier den Unterschied sieht.
    onKey(uppercase ? key.toUpperCase() : key, { shiftKey: shift });
    if (shift) setShift(false);
  };

  // Beide Umschalttasten sitzen in der unteren Buchstabenreihe, links vor dem
  // "<" und rechts hinter dem "-" – so wie auf der Tastatur vor dem Kind. Sie
  // standen zuerst in der Leertastenreihe; das war schlicht falsch abgemalt.
  //
  // Festgemacht wird das an der KENNUNG der Reihe, nicht an ihrer Nummer. Als
  // hier `i === 2` stand, hätte eine Ziffernreihe davor die Umschalttasten
  // stillschweigend an die Grundreihe geschoben.
  const shiftKey = (side: "links" | "rechts") => (
    <button
      key={`shift-${side}`}
      type="button"
      aria-label={`Umschalttaste ${side}`}
      aria-pressed={shift}
      className={[
        own.key,
        own.modKey,
        own.wideKey,
        shift ? own.modOn : "",
        wantedSide === side ? `${own.expected} pulse` : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={() => setShift((on) => !on)}
    >
      ⇧
    </button>
  );

  return (
    <div className={own.keyboard}>
      {bundle.keyboard.rows.map((row) => (
        <div key={row.id} className={own.keyRow}>
          {row.id === "grund" && (
            <button
              type="button"
              aria-label="Feststelltaste"
              aria-pressed={caps}
              className={[
                own.key,
                own.modKey,
                own.wideKey,
                caps ? own.modOn : "",
                wantedCaps ? `${own.expected} pulse` : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => setCaps((on) => !on)}
            >
              ⇪
            </button>
          )}
          {row.id === "unten" && shiftKey("links")}
          {row.keys.map((key) => {
            // Die Leertaste ist breit und heißt beim Namen – sonst stünde dort
            // ein Leerzeichen, das man nicht sieht.
            const isSpace = key === " ";
            const known = learned.has(key);
            const finger = known ? fingerFor(bundle, key) : null;
            const isExpected = key === expectedKey;
            const face = uppercase ? key.toUpperCase() : key;
            return (
              <button
                key={key}
                type="button"
                aria-label={isSpace ? "Leertaste" : face}
                className={[
                  own.key,
                  isSpace ? own.spaceKey : "",
                  known ? "" : own.unlearned,
                  isExpected ? `${own.expected} pulse` : "",
                  key === lastKey ? own.flash : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={cssVar({ "--finger-color": finger?.color })}
                onClick={() => press(key)}
              >
                {isSpace ? "Leertaste" : face}
                {(key === "f" || key === "j") && <span className={own.bump} />}
              </button>
            );
          })}
          {row.id === "unten" && shiftKey("rechts")}
        </div>
      ))}
    </div>
  );
}
